/**
 * Fixture data for widget previews + Storybook stories (Phase 2 onward).
 *
 * Inspired by the existing Digital Twin Concept demo's `simulationStore` —
 * synthetic foundry numbers with realistic ranges. When Storybook lands in
 * Phase 2 these become the default story args; when `packs/foundry/demo-data/`
 * lands they migrate there as JSON.
 */

import type {
  AlarmItem,
  EventItem,
  TimeseriesPoint,
  TimeseriesSeries,
} from "./types";

/** Generate `count` points stepping back `stepMs` ms from `endIso`. */
export function makeSeriesPoints(
  endIso: string,
  count: number,
  stepMs: number,
  generator: (i: number) => number,
): TimeseriesPoint[] {
  const end = new Date(endIso).getTime();
  return Array.from({ length: count }, (_, i) => {
    const t = new Date(end - (count - 1 - i) * stepMs).toISOString();
    return { t, v: generator(i) };
  });
}

const NOW_ISO = "2026-05-24T11:00:00.000Z";

// --- Foundry-flavoured time-series fixtures ---

export const FX_BATH_TEMP: TimeseriesSeries = {
  id: "tag-furn1-bath-temp",
  label: "Furnace 1 bath temperature",
  unit: "°C",
  color: "rgb(var(--color-material-molten-iron))",
  referenceRange: { min: 1380, max: 1480 },
  thresholds: [
    { value: 1500, label: "High limit", kind: "danger" },
    { value: 1350, label: "Low limit", kind: "warning" },
  ],
  points: makeSeriesPoints(NOW_ISO, 60, 60_000, (i) => {
    const base = 1420 + Math.sin(i / 4) * 25;
    const noise = (Math.random() - 0.5) * 8;
    return Math.round((base + noise) * 10) / 10;
  }),
};

export const FX_POWER_KW: TimeseriesSeries = {
  id: "tag-furn1-power-kw",
  label: "Furnace 1 power",
  unit: "kW",
  color: "rgb(var(--color-warning))",
  points: makeSeriesPoints(NOW_ISO, 60, 60_000, (i) => {
    const cycles = Math.sin(i / 6) * 250;
    return Math.max(0, Math.round(700 + cycles + (Math.random() - 0.5) * 40));
  }),
};

export const FX_SAND_MOISTURE: TimeseriesSeries = {
  id: "tag-sand-moisture",
  label: "Green sand moisture",
  unit: "%",
  color: "rgb(var(--color-material-green-sand))",
  referenceRange: { min: 3.4, max: 4.2 },
  points: makeSeriesPoints(NOW_ISO, 30, 120_000, (i) => {
    const drift = Math.sin(i / 5) * 0.4;
    return Math.round((3.8 + drift + (Math.random() - 0.5) * 0.1) * 100) / 100;
  }),
};

export const FX_KPI_SPARK: TimeseriesPoint[] = makeSeriesPoints(
  NOW_ISO,
  20,
  60_000,
  (i) => 12 + Math.sin(i / 2) * 2 + Math.random(),
);

export const FX_ALARMS: AlarmItem[] = [
  {
    id: "a-1",
    t: "2026-05-24T10:48:15.000Z",
    severity: "danger",
    source: "furnace_1 · bath_temp",
    title: "Bath temperature above high limit",
    description: "Reading 1512 °C — above 1500 °C limit for 38 s.",
    tags: ["furnace_1", "melt-shop"],
    acknowledged: false,
    active: true,
  },
  {
    id: "a-2",
    t: "2026-05-24T10:42:03.000Z",
    severity: "warning",
    source: "sand_mixer_1 · moisture",
    title: "Sand moisture trending high",
    description: "Last 6 heats above 4.2%. Porosity risk elevated.",
    tags: ["sand-prep", "porosity-risk"],
    acknowledged: false,
    active: true,
  },
  {
    id: "a-3",
    t: "2026-05-24T10:31:02.000Z",
    severity: "warning",
    source: "moulding_line_1 · throughput",
    title: "Moulding line throughput dropped",
    description: "12 moulds / hour over last 5 min (target 18).",
    tags: ["moulding-line", "shift-2"],
    acknowledged: true,
    acknowledgedBy: { user: "S. Bose", at: "2026-05-24T10:34:18.000Z" },
    active: true,
  },
  {
    id: "a-4",
    t: "2026-05-24T10:18:44.000Z",
    severity: "danger",
    source: "pour_station_1 · pour_temperature",
    title: "Pour temperature below low limit",
    description: "1352 °C — below 1360 °C minimum. Misrun risk.",
    tags: ["pour-station", "misrun-risk"],
    acknowledged: true,
    acknowledgedBy: { user: "S. Bose", at: "2026-05-24T10:21:09.000Z" },
    active: true,
  },
  {
    id: "a-5",
    t: "2026-05-24T09:55:30.000Z",
    severity: "info",
    source: "spectrometer · carbon",
    title: "Carbon outside target window",
    description: "Heat H-04812: C 2.92% (target 3.0–3.3%). Mechanicals retest scheduled.",
    tags: ["chemistry", "heat-04812"],
    acknowledged: true,
    acknowledgedBy: { user: "R. Iyer", at: "2026-05-24T09:58:45.000Z" },
    active: true,
  },
  {
    id: "a-6",
    t: "2026-05-24T09:14:55.000Z",
    severity: "warning",
    source: "sand_tester · moisture",
    title: "Sand moisture out of band",
    description: "Reading 4.6% — above 4.2% target. Auto-cleared after re-mull.",
    tags: ["sand-prep"],
    acknowledged: true,
    acknowledgedBy: { user: "R. Iyer", at: "2026-05-24T09:18:02.000Z" },
    active: false,
  },
  {
    id: "a-7",
    t: "2026-05-24T08:42:11.000Z",
    severity: "warning",
    source: "moulding_machine_1 · squeeze_pressure",
    title: "Squeeze pressure below band",
    description: "6.4 bar — below 7 bar target. Shrinkage risk on next 30 moulds.",
    tags: ["moulding-line"],
    acknowledged: true,
    acknowledgedBy: { user: "S. Bose", at: "2026-05-24T08:46:00.000Z" },
    active: false,
  },
  {
    id: "a-8",
    t: "2026-05-24T08:02:11.000Z",
    severity: "info",
    source: "gateway · g-04a2",
    title: "Gateway reconnected",
    description: "Re-established MQTT session after 14 s outage.",
    tags: ["infra"],
    acknowledged: true,
    acknowledgedBy: { user: "auto", at: "2026-05-24T08:02:14.000Z" },
    active: false,
  },
  {
    id: "a-9",
    t: "2026-05-23T22:11:08.000Z",
    severity: "info",
    source: "gateway · g-04a2",
    title: "Cert renewal scheduled",
    description: "Gateway certificate expires in 28 days. Automatic renewal queued.",
    tags: ["infra", "security"],
    acknowledged: true,
    acknowledgedBy: { user: "auto", at: "2026-05-23T22:11:09.000Z" },
    active: false,
  },
  {
    id: "a-10",
    t: "2026-05-23T16:14:00.000Z",
    severity: "danger",
    source: "tilt_furnace · last_seen",
    title: "Gateway offline",
    description: "Tilt Furnace (backup) gateway last seen 48 h ago. Check power + network.",
    tags: ["infra", "offline"],
    acknowledged: false,
    active: true,
  },
];

export const FX_EVENTS: EventItem[] = [
  {
    id: "e-1",
    t: "2026-05-24T10:58:21.000Z",
    severity: "info",
    source: "heat #4821",
    title: "Heat tapped",
    description: "Furnace 1 → ladle. 980 kg @ 1438 °C.",
    tags: ["heat-4821"],
  },
  {
    id: "e-2",
    t: "2026-05-24T10:42:11.000Z",
    severity: "success",
    source: "heat #4821 · qc",
    title: "Spectrometry within spec",
    description: "C 3.45%, Si 2.21%, Mn 0.62%, S 0.018%, P 0.043%.",
    tags: ["heat-4821", "qc"],
  },
  {
    id: "e-3",
    t: "2026-05-24T10:33:50.000Z",
    severity: "warning",
    source: "moulding_line_1",
    title: "Sand reclamation low",
    description: "Return-sand buffer below 60% capacity.",
    tags: ["sand-prep"],
  },
  {
    id: "e-4",
    t: "2026-05-24T10:05:00.000Z",
    severity: "info",
    source: "shift change",
    title: "Shift 2 handover",
    description: "S. Bose took over from R. Iyer.",
  },
];

// --- Device-list fixture (used in Table preview) ---

export interface DemoDeviceRow {
  id: string;
  name: string;
  site: string;
  /**
   * Display-only *field protocol* label. P6.6 widened this from a fixed
   * union to a free string: the live Things page derives it from the
   * Thing's gateway product (modbus-tcp | modbus-rtu | multi-protocol |
   * generic-mqtt | …) rather than the old 4-value enum.
   * Fixtures still use the legacy labels.
   */
  protocol: string;
  tags: number;
  msgPerSec: number;
  msgPerSecHistory: TimeseriesPoint[];
  lastSeen: string;
  status: "online" | "offline" | "warning" | "error" | "unknown";
  certExpiresAt: string;
  twinEntityId: string | null;
  twinEntityName: string | null;
}

/** Generate a deterministic msg/s trend so the SparklineCell looks alive. */
function makeMsgHistory(mean: number, count = 20): TimeseriesPoint[] {
  return Array.from({ length: count }, (_, i) => ({
    t: new Date(Date.UTC(2026, 4, 24, 10, 30 + i * 1)).toISOString(),
    v: Math.max(0, +(mean + (Math.sin(i / 2) + (Math.random() - 0.5)) * mean * 0.18).toFixed(2)),
  }));
}

/**
 * Dashboard metadata fixtures — drive the `/app/dashboards` index page
 * until the dashboard editor lands in B0.9.
 */
export type DashboardSource = "my" | "pack" | "shared";

export interface DemoDashboardRow {
  id: string;
  name: string;
  description: string;
  source: DashboardSource;
  pack?: string;
  owner: string;
  widgetCount: number;
  updatedAt: string;
  thumbnailHint: "kpi-grid" | "timeseries" | "process-graph" | "table" | "mixed";
}

export const FX_DASHBOARDS: DemoDashboardRow[] = [
  {
    id: "dash-plant-overview",
    name: "Plant overview",
    description: "Day-shift KPIs, active heats, alarm feed.",
    source: "my",
    owner: "you",
    widgetCount: 8,
    updatedAt: "2026-05-24T09:12:00.000Z",
    thumbnailHint: "mixed",
  },
  {
    id: "dash-operator-overview",
    name: "Operator overview",
    description: "Standard shift dashboard for foundry operators — heat status, OEE, alarm feed.",
    source: "pack",
    pack: "foundry",
    owner: "SAHAS Labs",
    widgetCount: 10,
    updatedAt: "2026-05-20T00:00:00.000Z",
    thumbnailHint: "mixed",
  },
  {
    id: "dash-melt-shop",
    name: "Melt shop",
    description: "Per-furnace heat timeline, bath temperature, power draw, energy per heat.",
    source: "pack",
    pack: "foundry",
    owner: "SAHAS Labs",
    widgetCount: 6,
    updatedAt: "2026-05-20T00:00:00.000Z",
    thumbnailHint: "timeseries",
  },
  {
    id: "dash-moulding-line",
    name: "Moulding line",
    description: "Sand testing panel, mould count, scrap rate, throughput.",
    source: "pack",
    pack: "foundry",
    owner: "SAHAS Labs",
    widgetCount: 7,
    updatedAt: "2026-05-20T00:00:00.000Z",
    thumbnailHint: "kpi-grid",
  },
  {
    id: "dash-qc-summary",
    name: "QC summary",
    description: "Defect Pareto, mechanical test results, chemistry trends.",
    source: "pack",
    pack: "foundry",
    owner: "SAHAS Labs",
    widgetCount: 5,
    updatedAt: "2026-05-20T00:00:00.000Z",
    thumbnailHint: "table",
  },
  {
    id: "dash-energy",
    name: "Energy",
    description: "Per-station kWh, waste %, furnace share, current power draw.",
    source: "pack",
    pack: "foundry",
    owner: "SAHAS Labs",
    widgetCount: 4,
    updatedAt: "2026-05-20T00:00:00.000Z",
    thumbnailHint: "kpi-grid",
  },
  {
    id: "dash-shift-handover",
    name: "Shift handover",
    description: "Shift-end summary shared with the incoming shift lead.",
    source: "shared",
    owner: "R. Iyer",
    widgetCount: 5,
    updatedAt: "2026-05-23T18:00:00.000Z",
    thumbnailHint: "mixed",
  },
];

export const FX_DEVICES: DemoDeviceRow[] = [
  {
    id: "d-1",
    name: "Furnace 1 PLC",
    site: "Pune-1",
    protocol: "modbus-tcp",
    tags: 24,
    msgPerSec: 12.4,
    msgPerSecHistory: makeMsgHistory(12.4),
    lastSeen: "2026-05-24T10:59:55.000Z",
    status: "online",
    certExpiresAt: "2027-04-12T00:00:00.000Z",
    twinEntityId: "ent-furnace-1",
    twinEntityName: "Furnace 1",
  },
  {
    id: "d-2",
    name: "Furnace 2 PLC",
    site: "Pune-1",
    protocol: "modbus-tcp",
    tags: 24,
    msgPerSec: 11.9,
    msgPerSecHistory: makeMsgHistory(11.9),
    lastSeen: "2026-05-24T10:59:51.000Z",
    status: "online",
    certExpiresAt: "2027-04-12T00:00:00.000Z",
    twinEntityId: "ent-furnace-2",
    twinEntityName: "Furnace 2",
  },
  {
    id: "d-3",
    name: "DISA Moulding Line",
    site: "Pune-1",
    protocol: "opcua",
    tags: 48,
    msgPerSec: 22.6,
    msgPerSecHistory: makeMsgHistory(22.6),
    lastSeen: "2026-05-24T10:59:48.000Z",
    status: "warning",
    certExpiresAt: "2027-04-12T00:00:00.000Z",
    twinEntityId: "ent-moulding-1",
    twinEntityName: "DISA Line 1",
  },
  {
    id: "d-4",
    name: "Sand Mixer A",
    site: "Pune-1",
    protocol: "s7",
    tags: 12,
    msgPerSec: 4.1,
    msgPerSecHistory: makeMsgHistory(4.1),
    lastSeen: "2026-05-24T10:59:42.000Z",
    status: "online",
    certExpiresAt: "2027-02-28T00:00:00.000Z",
    twinEntityId: "ent-sand-mixer-1",
    twinEntityName: "Sand Mixer A",
  },
  {
    id: "d-5",
    name: "OES Spectrometer",
    site: "Pune-1",
    protocol: "modbus-tcp",
    tags: 8,
    msgPerSec: 0.3,
    msgPerSecHistory: makeMsgHistory(0.3),
    lastSeen: "2026-05-24T10:46:11.000Z",
    status: "warning",
    certExpiresAt: "2027-01-15T00:00:00.000Z",
    twinEntityId: "ent-spectro-1",
    twinEntityName: "OES Spectrometer",
  },
  {
    id: "d-6",
    name: "Versatile Sand Tester VUD-III",
    site: "Pune-1",
    protocol: "modbus-tcp",
    tags: 6,
    msgPerSec: 0.2,
    msgPerSecHistory: makeMsgHistory(0.2),
    lastSeen: "2026-05-24T10:58:02.000Z",
    status: "online",
    certExpiresAt: "2027-03-04T00:00:00.000Z",
    twinEntityId: "ent-sand-tester-1",
    twinEntityName: "Sand Tester VUD-III",
  },
  {
    id: "d-7",
    name: "Tilt Furnace (backup)",
    site: "Pune-1",
    protocol: "modbus-tcp",
    tags: 18,
    msgPerSec: 0,
    msgPerSecHistory: makeMsgHistory(0).map((p) => ({ ...p, v: 0 })),
    lastSeen: "2026-05-22T16:14:00.000Z",
    status: "offline",
    certExpiresAt: "2027-05-01T00:00:00.000Z",
    twinEntityId: null,
    twinEntityName: null,
  },
  {
    id: "d-8",
    name: "Shot Blast Cabinet",
    site: "Pune-1",
    protocol: "bacnet",
    tags: 5,
    msgPerSec: 0.1,
    msgPerSecHistory: makeMsgHistory(0.1),
    lastSeen: "2026-05-24T10:58:55.000Z",
    status: "online",
    certExpiresAt: "2027-06-12T00:00:00.000Z",
    twinEntityId: null,
    twinEntityName: null,
  },
];
